// frontend/src/components/Tenants.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './Tenants.css';
import { useNavigate } from 'react-router-dom';
import {
  FaUserPlus, FaSearch, FaFileInvoiceDollar,
  FaBell, FaCalendarCheck
} from 'react-icons/fa';
// Import professional FontAwesome icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

const BASE_URL = "https://ikaze-thouse-backend.vercel.app";

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [totalRent, setTotalRent] = useState(0);
  const [isUpdatingAvailability, setIsUpdatingAvailability] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState('');
  const [isSendingReminders, setIsSendingReminders] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 6;

  const navigate = useNavigate();

  useEffect(() => {
    fetchTenants();
    handleSendDepartureReminders();
  }, []);

  useEffect(() => {
    const sum = tenants.reduce((acc, t) => acc + (parseFloat(t.rent_rwf) || 0), 0);
    setTotalRent(sum.toLocaleString());
  }, [tenants]);

  const fetchTenants = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/v1/tenants/getAll`);
      const sorted = (res.data.data || []).sort((a, b) => b.tenant_id - a.tenant_id);
      setTenants(sorted);
    } catch (err) {
      setError('Failed to fetch data');
    }
  };

  const handleSendDepartureReminders = async () => {
    setIsSendingReminders(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/v1/tenants/reminders?unit=days&value=3`);
      setReminderMessage(res.data.message);
    } catch {
      setReminderMessage('Reminders sync failed');
    }
    setIsSendingReminders(false);
  };

  const handleMarkApartmentsAvailable = async () => {
    setIsUpdatingAvailability(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/v1/tenants/mark-available`);
      setAvailabilityMessage(res.data.message);
      fetchTenants();
    } catch {
      setAvailabilityMessage('Update failed');
    }
    setIsUpdatingAvailability(false);
  };

  const filteredTenants = tenants.filter(t =>
    t.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.appartment_unite.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLast = currentPage * recordsPerPage;
  const indexOfFirst = indexOfLast - recordsPerPage;
  const currentRecords = filteredTenants.slice(indexOfFirst, indexOfLast);
  const nPages = Math.ceil(filteredTenants.length / recordsPerPage);

  return (
    <div className="tenant-management-page">
      <header className="page-header">
        <div className="header-text">
          <h1>Tenant Management</h1>
          <p>Real-time oversight of residents and lease status.</p>
        </div>
        <button className="add-btn-main" onClick={() => navigate('/add-tenant')}>
          <FaUserPlus /> Add New Tenant
        </button>
      </header>

      <div className="stats-grid">
        <div className="stat-card revenue">
          <div className="stat-icon"><FaFileInvoiceDollar /></div>
          <div className="stat-info">
            <label>Projected Revenue</label>
            <h3>{totalRent} RWF</h3>
          </div>
        </div>
        {/* 💰 COUNT: RWF TENANTS */}
        <div className="stat-card revenue">
          <div className="stat-icon">
            <FaFileInvoiceDollar />
          </div>
          <div className="stat-info">
            <label>RWF Currency Records</label>
            <h3>
              {tenants.filter(t => t.rent_rwf).length.toLocaleString()}
            </h3>
          </div>
        </div>

        {/* 💵 COUNT: USD TENANTS */}
        <div className="stat-card usd">
          <div className="stat-icon">
            <FaFileInvoiceDollar />
          </div>
          <div className="stat-info">
            <label>USD Currency Records</label>
            <h3>
              {tenants.filter(t => t.rent_usd).length.toLocaleString()}
            </h3>
          </div>
        </div>
        {/* <div className="stat-card reminder">
          <div className="stat-icon"><FaBell /></div>
          <div className="stat-info">
            <label>Automated Alerts</label>
            <p>{isSendingReminders ? "Syncing..." : reminderMessage || "Active"}</p>
          </div>
        </div> */}
        <div className="stat-card action-card">
          <div className="stat-icon"><FaCalendarCheck /></div>
          <div className="stat-info">
            <button className="sync-btn" onClick={handleMarkApartmentsAvailable} disabled={isUpdatingAvailability}>
              {isUpdatingAvailability ? "Syncing..." : "Update Availability"}
            </button>
            <p className="mini-msg">{availabilityMessage}</p>
          </div>
        </div>
      </div>

      <div className="data-section">
        <div className="table-controls">
          <div className="search-box">
            <FaSearch />
            <input
              type="text"
              placeholder="Filter by name or unit..."
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="custom-table-container">
          <table className="modern-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Guest Details</th>
                <th>Duration</th>
                <th className="unit-header">Unit</th>
                <th>Rent in USD</th>
                <th>Rent in RWF</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentRecords.map(t => {
                const isPast = new Date(t.departure_date) < new Date();
                return (
                  <tr key={t.tenant_id} className="table-row-hover">
                    <td><span className="id-badge">#{t.tenant_id}</span></td>
                    <td>
                      <div className="guest-cell">
                        <div className="avatar">{t.guest_name.charAt(0)}</div>
                        <div className="guest-info">
                          <span className="guest-name">{t.guest_name}</span>
                          <span className={`status-pill ${isPast ? 'status-past' : 'status-active'}`}>
                            {isPast ? 'Terminated' : 'Active'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="date-range">
                        <small><strong>IN:</strong> {new Date(t.arrival_date).toLocaleDateString()}</small>
                        <small><strong>OUT:</strong> {new Date(t.departure_date).toLocaleDateString()}</small>
                      </div>
                    </td>
                    <td className="unit-column">
                      <span className="unit-badge">{t.appartment_unite}</span>
                    </td>
                    <td className="rent-amount">${parseFloat(t.rent_usd).toLocaleString()}</td>
                    <td className="rent-amount">{parseFloat(t.rent_rwf).toLocaleString()} RWF</td>
                    <td>
                      {/* --- REDESIGNED ICON ACTION BUTTONS --- */}
                      <div className="action-icons-group">
                        <button className="icon-btn view" title="View Details" onClick={() => navigate(`/manager/tenant/view/${t.tenant_id}`)}>
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button className="icon-btn edit" title="Edit Tenant" onClick={() => navigate(`/manager/tenant/edit/${t.tenant_id}`)}>
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button className="icon-btn delete" title="Delete Tenant" onClick={() => { if (window.confirm('Delete?')) axios.delete(`${BASE_URL}/api/v1/tenants/delete/${t.tenant_id}`).then(() => fetchTenants()) }}>
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="pagination-wrapper">
          <button className="pag-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
          <span className="page-indicator">Page {currentPage} of {nPages}</span>
          <button className="pag-btn" disabled={currentPage === nPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
};

export default Tenants;